import { useEffect, useState, useSyncExternalStore } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { useEscape } from '@core/shortcuts'
import {
  acceptRecovery,
  discardRecovery,
  getManifestoDoArranque,
  getRecoveryStatus,
  subscribeRecovery,
} from './recovery'
import {
  describeManifest,
  formatManifestDate,
  type RecoveryManifest,
} from './recoveryManifest'

/**
 * A pergunta do arranque: "recuperar a sessão anterior?".
 *
 * Aparece quando ficou um manifesto de trabalho por gravar (fecho da
 * aba, crash, recarregamento). Enquanto está no ecrã nada de trabalho
 * entra na app: os stores esperam pela resposta (ver recovery.ts).
 *
 *  - Recuperar: reabre os IFCs que ficaram em cache, hidrata os BCFs e
 *    os viewpoints, e as vistas 2D voltam quando o modelo delas carrega.
 *    A sessão fica outra vez marcada como POR GRAVAR.
 *  - Descartar: apaga o trabalho todo (IndexedDB incluído) e o
 *    manifesto. As preferências de UI ficam intactas.
 */
export default function RecoveryPrompt() {
  const status = useSyncExternalStore(
    subscribeRecovery,
    getRecoveryStatus,
    getRecoveryStatus,
  )
  const [restoring, setRestoring] = useState(false)
  const [stage, setStage] = useState('')
  const [busy, setBusy] = useState(false)
  // Escape: "decidir mais tarde". O aviso sai da frente sem recuperar nem
  // descartar (a fase continua 'pending') e fica um lembrete para o
  // reabrir.
  const [adiado, setAdiado] = useState(false)
  useEscape(status.phase === 'pending' && !adiado && !busy, () => setAdiado(true))

  // O que o aviso mostra fica fixado quando ele aparece: o manifesto do
  // arranque, que é o que a sessão ANTERIOR deixou. O `status.manifest`
  // continua a ser reescrito enquanto o aviso está aberto, e os IFC que
  // se abrissem nesta sessão apareciam na lista da anterior. Fixa-se
  // também o momento, para o Recuperar não reabrir o que esta sessão
  // guardou depois.
  const [fixado, setFixado] = useState<{
    manifest: RecoveryManifest | null
    em: string
  } | null>(null)
  if (status.phase === 'pending' && fixado === null) {
    setFixado({
      manifest: getManifestoDoArranque() ?? status.manifest,
      em: new Date().toISOString(),
    })
  }
  const fixadoEm = fixado?.em ?? null

  const loaderReady = useSyncExternalStore(
    (cb) => viewerApi.subscribe(cb),
    () => viewerApi.hasIfcLoader(),
    () => false,
  )

  // Reposição do conteúdo, depois de o utilizador aceitar. Espera que a
  // tela do viewer registe o carregador: aceitar acontece muitas vezes
  // antes de o WebGL estar de pé.
  useEffect(() => {
    if (!restoring) return
    // Sem carregador ainda não há onde pôr os IFCs; o efeito volta a
    // correr quando o viewerApi registar a tela.
    if (!loaderReady) return
    let cancelled = false
    // O lote inteiro conta como "a carregar" (também entre um IFC e o
    // seguinte): quem lê os modelos em segundo plano espera pelo fim.
    const fecharLote = viewerApi.iniciarLoteDeCarregamento()
    void (async () => {
      try {
        const cached = await ifcPersistence.list()
        cached.sort((a, b) => (a.importedAt < b.importedAt ? -1 : 1))
        for (const rec of cached) {
          if (cancelled) return
          // Só o que a sessão anterior deixou: o que esta sessão guardou
          // depois de o aviso aparecer, ou o que já está aberto, não se
          // reabre (dava o mesmo modelo duas vezes).
          if (fixadoEm && rec.importedAt >= fixadoEm) continue
          const jaAberto = useAppStore
            .getState()
            .models.some((m) => m.sha256.toLowerCase() === rec.sha256.toLowerCase())
          if (jaAberto) continue
          setStage(`a reabrir ${rec.filename}…`)
          try {
            const aberto = await viewerApi.loadIfcFile(ifcPersistence.toFile(rec), {
              replace: false,
            })
            if (aberto.ok) void ifcPersistence.touch(rec.sha256)
            else console.warn('[recovery] falhou reabrir', rec.filename, aberto.erro)
          } catch (err) {
            console.warn('[recovery] falhou reabrir', rec.filename, err)
          }
        }
        if (cancelled) return
        setStage('a repor BCFs e viewpoints…')
        const store = useAppStore.getState()
        try {
          await store.bcfHydrate()
        } catch (err) {
          console.warn('[recovery] bcfHydrate falhou', err)
        }
        try {
          await store.refreshViewpoints()
        } catch (err) {
          console.warn('[recovery] refreshViewpoints falhou', err)
        }
      } finally {
        fecharLote()
        if (!cancelled) {
          setRestoring(false)
          setStage('')
        }
      }
    })()
    return () => {
      cancelled = true
      fecharLote()
    }
  }, [restoring, loaderReady, fixadoEm])

  if (status.phase !== 'pending') {
    if (!restoring) return null
    const detalhe = loaderReady ? stage : 'à espera do viewer…'
    return (
      <div
        data-testid="recovery-progress"
        className="pointer-events-none fixed bottom-3 left-1/2 z-[1100] -translate-x-1/2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs text-[var(--color-text-muted)] shadow-lg"
      >
        A recuperar a sessão {detalhe ? `· ${detalhe}` : ''}
      </div>
    )
  }

  // O que se mostra é o que ficou fixado quando o aviso apareceu.
  const mostrado = fixado ? fixado.manifest : status.manifest
  const parts = describeManifest(mostrado)
  const quando = formatManifestDate(mostrado)

  const onRecuperar = () => {
    setBusy(true)
    acceptRecovery()
    setRestoring(true)
    setBusy(false)
  }

  const onDescartar = async () => {
    const ok = window.confirm(
      'Descartar o trabalho da sessão anterior? Os IFCs, BCFs, vistas 2D e viewpoints que ficaram por gravar são apagados. As tuas preferências não são tocadas.',
    )
    if (!ok) return
    setBusy(true)
    await discardRecovery()
    setBusy(false)
  }

  // Adiado com Escape: um lembrete discreto em vez do aviso. As leituras
  // do trabalho anterior continuam à espera da decisão, como antes.
  if (adiado) {
    return (
      <div
        data-testid="recovery-deferred"
        className="fixed bottom-3 left-1/2 z-[1100] flex -translate-x-1/2 items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs text-[var(--color-text-muted)] shadow-lg"
      >
        <AlertTriangle size={14} className="shrink-0 text-[var(--color-accent)]" aria-hidden />
        <span>Há trabalho de uma sessão anterior por decidir.</span>
        <button
          type="button"
          onClick={() => setAdiado(false)}
          className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text)] hover:bg-[var(--color-bg)]"
        >
          Decidir
        </button>
      </div>
    )
  }

  return (
    // Banda dos diálogos (CAMADA.dialogos), ver core/layout/camadas.ts.
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-title"
      data-testid="recovery-prompt"
    >
      <div className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <AlertTriangle
            size={18}
            className="mt-0.5 shrink-0 text-[var(--color-accent)]"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <h2
              id="recovery-title"
              className="text-sm font-semibold text-[var(--color-text)]"
            >
              Recuperar a sessão anterior?
            </h2>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              Ficou trabalho por gravar
              {quando ? ` de ${quando}` : ''}. Nada foi reposto: decide tu.
            </p>
            {parts.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {parts.map((p) => (
                  <li
                    key={p}
                    className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] text-[var(--color-text)]"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            )}
            {mostrado && mostrado.ifcNames.length > 0 && (
              <ul className="mt-2 max-h-24 overflow-auto font-mono text-[11px] text-[var(--color-text-muted)]">
                {mostrado.ifcNames.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
              O trabalho só fica guardado a sério com "Guardar sessão"
              (.vn2r-session). Isto aqui é a rede de segurança para quando
              a aba fecha sem gravar.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => void onDescartar()}
            disabled={busy}
            className="rounded border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] disabled:opacity-50"
          >
            Descartar
          </button>
          <button
            type="button"
            onClick={onRecuperar}
            disabled={busy}
            className="rounded bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-[var(--color-bg)] hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
          >
            Recuperar
          </button>
        </div>
      </div>
    </div>
  )
}
